import { Alert, Button, Col, Empty, Row, Skeleton, message } from 'antd';
import clsx from 'clsx';
import { includes } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../AppContext';
import { AddButton, InfoIcon } from '../../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../../common/CommonUseLazyQuery';
import {
  ADDONS,
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
} from '../../../../../../../common/constants';
import useRouter from '../../../../../../../common/useRouter';
import CanPerform from '../../../../../../../components/CanPerform';
import CommonCard from '../../../../../../../components/CommonCard';
import { ORGANIZATION_INSTRUCTION_TYPE_LIST } from '../graphql/Queries';
import AddEditModal from './AddEditModal';
import CreateModal from './CreateModal';
import DeleteModal from './DeleteModal';
import InstructionTypeTree from './InstructionTypeTree';

const InstructionType = ({ projectData }) => {
  const { getTenantUser } = useContext(AppContext);
  const isProjectActive = projectData?.isActive;
  const [instructionTypeList, setInstructionTypeList] = useState([]);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTree, setSelectedTree] = useState();
  const [checkedKeys, setCheckedKeys] = useState();
  const [loading, setLoading] = useState(true);
  const [isDisabledNodeSelected, setIsDisabledNodeSelected] = useState(false);
  const [checkedTreeNode, setCheckedTreeNode] = useState();
  const [openNodes, setOpenNodes] = useState({});
  const {
    params: { projectId },
  } = useRouter();
  const tenantUser = getTenantUser();
  const maxLevel0 = Number(
    process.env.REACT_APP_INSTRUCTION_TYPE_MAX_LEVEL_0 || 4,
  );
  const isInstructionTypeActive = includes(
    tenantUser?.tenant?.addOn,
    ADDONS?.INSTRUCTION_TYPE,
  );

  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const [organizationInstructionTypeList] = useLazyQueryWithCallbacks(
    ORGANIZATION_INSTRUCTION_TYPE_LIST,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        setInstructionTypeList(res?.organizationInstructionTypeList);
        setLoading(false);
        setCheckedTreeNode();
      },
      onError() {},
    },
  );

  const handleRefetch = () => {
    if (isInstructionTypeActive) {
      organizationInstructionTypeList();
    }
  };

  useEffect(() => {
    handleRefetch();
  }, [isInstructionTypeActive]);

  const renderTreeView = () => {
    return (
      <InstructionTypeTree
        setOpenNodes={setOpenNodes}
        openNodes={openNodes}
        instructionTypeList={instructionTypeList}
        setInstructionTypeList={setInstructionTypeList}
        setShowAddEditModal={setShowAddEditModal}
        setSelectedTree={setSelectedTree}
        selectedTree={selectedTree}
        setCheckedTreeNode={setCheckedTreeNode}
        checkedTreeNode={checkedTreeNode}
        setIsDisabledNodeSelected={setIsDisabledNodeSelected}
        isDisabledNodeSelected={isDisabledNodeSelected}
        handleRefetch={handleRefetch}
        isProjectActive={isProjectActive}
      />
    );
  };

  return (
    <>
      {isInstructionTypeActive ? (
        <div className="project-tab-details instruction-type-module">
          <Row
            gutter={10}
            justify="space-between"
            align="middle"
            className="top-row"
          >
            <Col span={isDesktopViewport ? 6 : 24}>
              <div id="search-div" />
            </Col>
            <Col
              span={isDesktopViewport ? 18 : 24}
              className="d-flex align-center justify-end"
            >
              <div className="d-flex justify-end main-action-buttons align-center">
                {isInstructionTypeActive && (
                  <CanPerform
                    action={ALLOWED_ACTION_KEYS.EDIT_NOMENCLATURE}
                    type={ALLOWED_ACTION_TYPE.BOTH}
                  >
                    {isDesktopViewport && (
                      <Button
                        shape="round"
                        icon={
                          <AddButton
                            className={clsx(isDesktopViewport && 'mr-5')}
                            height={20}
                            width={20}
                          />
                        }
                        type="primary"
                        className="mr-10"
                        disabled={
                          instructionTypeList?.filter(
                            (item) => item?.isActive && item?.level === 0,
                          )?.length >= maxLevel0
                        }
                        onClick={() => {
                          if (isDesktopViewport) {
                            if (instructionTypeList?.length > 0) {
                              setSelectedTree();
                              setShowAddEditModal(true);
                            } else {
                              setShowCreateModal(true);
                            }
                          } else {
                            message.destroy();
                            message.warning({
                              content:
                                'Editing allowed only from desktop web browser',
                              icon: (
                                <InfoIcon
                                  height={18}
                                  width={18}
                                  className="mr-5"
                                />
                              ),
                              className: 'instruction-type-mobile-info',
                            });
                          }
                        }}
                      >
                        {isDesktopViewport &&
                          `${instructionTypeList?.length > 0 ? 'Add L0' : 'Create'}`}
                      </Button>
                    )}
                  </CanPerform>
                )}
              </div>
            </Col>
          </Row>
          {loading && <Skeleton active />}
          {instructionTypeList?.length > 0 ? (
            renderTreeView()
          ) : (
            <CommonCard>
              <Empty />
            </CommonCard>
          )}
        </div>
      ) : (
        <div className="d-flex justify-center p-10">
          <Alert
            className="max-width-500"
            title="Your organization doesn’t have access to the Instruction Type feature."
            description={
              <>
                <div>
                  This tenant doesn’t have access to the Instruction Type
                  feature.
                </div>
                <div>Please contact your system admin to change.</div>
              </>
            }
            type="warning"
            showIcon
          />
        </div>
      )}

      {showAddEditModal && (
        <AddEditModal
          showModal={showAddEditModal}
          setShowModal={setShowAddEditModal}
          projectId={projectId}
          handleRefetch={handleRefetch}
          selectedTree={selectedTree}
          setSelectedTree={setSelectedTree}
        />
      )}
      {showDeleteModal && (
        <DeleteModal
          showModal={showDeleteModal}
          setShowModal={setShowDeleteModal}
          projectId={projectId}
          handleRefetch={handleRefetch}
          setCheckedTreeNode={setCheckedTreeNode}
          checkedTreeNode={checkedTreeNode}
          setCheckedKeys={setCheckedKeys}
          checkedKeys={checkedKeys}
        />
      )}
      {showCreateModal && (
        <CreateModal
          showModal={showCreateModal}
          setShowModal={setShowCreateModal}
          handleClickEvent={() => {
            setSelectedTree();
            setShowAddEditModal(true);
          }}
        />
      )}
    </>
  );
};

export default InstructionType;
